import { Contract, ethers } from "ethers";
import * as chains from "./constants/chains";

import Web3 from "web3";

const ROUTER = require("./abi/Router.json");
const ERC20 = require("./build/ERC20.json");
const FACTORY = require("./build/IUniswapV2Factory.json");
const PAIR = require("./build/IUniswapV2Pair.json");

export function getProvider() {
  return new ethers.providers.Web3Provider(window.ethereum);
}

export function getSigner(provider) {
  return provider.getSigner();
}

export async function getNetwork() {
  const chainId = await window.ethereum.request({
    method: "eth_chainId",
  });

  return parseInt(chainId, 16);
}

export async function checkNetwork(provider) {
  const chainId = getNetwork(provider);
  if (chains.networks.includes(chainId)) {
    return true;
  }
  return false;
}

export async function getWeth(contractAddress) {
  const web3 = new Web3(window.ethereum);
  let contract = new web3.eth.Contract(ROUTER.abi, contractAddress);
  return await contract.methods.WETH().call();
}

export async function getFactory(contractAddress) {
  const web3 = new Web3(window.ethereum);
  let contract = new web3.eth.Contract(ROUTER.abi, contractAddress);
  return await contract.methods.factory().call();
}

export async function getAccount() {
  let account = "";
  await window.ethereum
    .request({
      method: "eth_requestAccounts",
    })
    .then((accounts) => {
      account = accounts[0];
    });

  return account;
}

//This function checks if a ERC20 token exists for a given address
//    `address` - The Ethereum address to be checked
//    `signer` - The current signer
export function doesTokenExist(address) {
  try {
    const web3 = new Web3(window.ethereum);
    return new web3.eth.Contract(ERC20.abi, address);
  } catch (err) {
    return false;
  }
}

export async function getDecimals(token) {
  const decimals = await token.methods
    .decimals()
    .call()
    .then((result) => {
      return result;
    })
    .catch((error) => {
      console.log("No tokenDecimals function for this token, set to 0");
      return 0;
    });
  return decimals;
}

export async function getSymbol(token) {
  return await token.methods.symbol().call();
}

export async function getName(token) {
  return await token.methods.name().call();
}

export async function balanceOf(token, accountAddress) {
  const decimals = await token.methods
    .balanceOf(accountAddress)
    .call()
    .then((result) => {
      return result;
    })
    .catch((error) => {
      return 0;
    });
  return decimals;
}

export async function getBalance(walletAddress) {
  const web3 = new Web3(window.ethereum);
  let result = await web3.eth.getBalance(walletAddress);
  result = web3.utils.fromWei(result, "ether");

  return result;
}

export async function getAllowance(token, accountAddress, spenderAddress) {
  return await token.methods.allowance(accountAddress, spenderAddress).call();
}

export async function setApprove(
  token,
  accountAddress,
  spenderAddress,
  amount
) {
  const web3 = new Web3(window.ethereum);

  let gas_price = await web3.eth.getGasPrice();
  let options = {
    from: accountAddress,
    gasPrice: gas_price * 1.5,
  };

  return await token.methods.approve(spenderAddress, amount).send(options);
}

// This function returns an object with 2 fields: `balance` which container's the account's balance in the particular token,
// and `symbol` which is the abbreviation of the token name. To work correctly it must be provided with 4 arguments:
//    `accountAddress` - An Ethereum address of the current user's account
//    `address` - An Ethereum address of the token to check for (either a token or AUT)
//    `provider` - The current provider
//    `signer` - The current signer
export async function getBalanceAndSymbol(
  accountAddress,
  address,
  weth_address,
  coins
) {
  try {
    const web3 = new Web3(window.ethereum);
    if (address === weth_address) {
      const balanceRaw = await getBalance(accountAddress);

      const token = new web3.eth.Contract(ERC20.abi, address);

      const tokenDecimals = await getDecimals(token);

      return {
        balance: balanceRaw,
        symbol: coins[0].abbr,
        name: coins[0].name,
        decimal: tokenDecimals,
      };
    } else {
      const token = new web3.eth.Contract(ERC20.abi, address);
      const tokenDecimals = await getDecimals(token);
      const balanceRaw = await balanceOf(token, accountAddress);
      const symbol = await getSymbol(token);
      const name = await getName(token);

      return {
        balance: balanceRaw * 10 ** -tokenDecimals,
        symbol: symbol,
        name: name,
        decimal: tokenDecimals,
      };
    }
  } catch (error) {
    console.log("The getBalanceAndSymbol function had an error!");
    console.log(error);
    return false;
  }
}

// This function swaps two particular tokens / AUT, it can handle switching from AUT to ERC20 token, ERC20 token to AUT, and ERC20 token to ERC20 token.
// No error handling is done, so any issues can be caught with the use of .catch()
// To work correctly, there needs to be 7 arguments:
//    `address1` - An Ethereum address of the token to trade from (either a token or AUT)
//    `address2` - An Ethereum address of the token to trade to (either a token or AUT)
//    `amount` - A float or similar number representing the value of address1's token to trade
//    `routerContract` - The router contract to carry out this trade
//    `accountAddress` - An Ethereum address of the current user's account
//    `signer` - The current signer
export async function swapTokens(
  address1,
  address2,
  amount,
  routerAddress,
  accountAddress,
  wethAddress,
  callback
) {
  const web3 = new Web3(window.ethereum);
  const tokens = [address1, address2];
  const time = Math.floor(Date.now() / 1000) + 200000;
  const deadline = ethers.BigNumber.from(time);

  const token1 = new web3.eth.Contract(ERC20.abi, address1);
  const tokenDecimals = await getDecimals(token1);

  const amountIn = ethers.utils.parseUnits(amount, tokenDecimals);

  let options = {
    from: accountAddress,
    gasPrice: await web3.eth.getGasPrice(),
    gas: 300000,
  };

  const routerContract = new web3.eth.Contract(ROUTER.abi, routerAddress);
  const amountOut = await routerContract.methods
    .getAmountsOut(amountIn, tokens)
    .call();

  await setApprove(token1, accountAddress, routerAddress, amountIn);

  let allowanceInterval = setInterval(async () => {
    const allowance1 = await getAllowance(
      token1,
      accountAddress,
      routerAddress
    );

    if (+allowance1 >= +amountIn) {
      clearInterval(allowanceInterval);
      if (address1 === wethAddress) {
        // Eth -> Token
        options.value = amountIn;

        await routerContract.methods
          .swapExactETHForTokens(amountOut[1], tokens, accountAddress, deadline)
          .send(options)
          .then(() => {
            callback({ status: true });
          })
          .catch((e) => {
            callback({ status: false, e });
          });
      } else if (address2 === wethAddress) {
        // Token -> Eth
        await routerContract.methods
          .swapExactTokensForETH(
            amountIn,
            amountOut[1],
            tokens,
            accountAddress,
            deadline
          )
          .send(options)
          .then(() => {
            callback({ status: true });
          })
          .catch((e) => {
            callback({ status: false, e });
          });
      } else {
        await routerContract.methods
          .swapExactTokensForTokens(
            amountIn,
            amountOut[1],
            tokens,
            accountAddress,
            deadline
          )
          .send(options)
          .then(() => {
            callback({ status: true });
          })
          .catch((e) => {
            callback({ status: false, e });
          });
      }
    }
  }, 5000);
}

//This function returns the conversion rate between two token addresses
//    `address1` - An Ethereum address of the token to swaped from (either a token or AUT)
//    `address2` - An Ethereum address of the token to swaped to (either a token or AUT)
//    `amountIn` - Amount of the token at address 1 to be swaped from
//    `routerContract` - The router contract to carry out this swap
export async function getAmountOut(
  address1,
  address2,
  amountIn,
  routerAddress
) {
  try {
    const web3 = new Web3(window.ethereum);
    const token1 = new web3.eth.Contract(ERC20.abi, address1);
    const token1Decimals = await getDecimals(token1);

    const token2 = new web3.eth.Contract(ERC20.abi, address2);
    const token2Decimals = await getDecimals(token2);

    const routerContract = new web3.eth.Contract(ROUTER.abi, routerAddress);

    const values_out = await routerContract.methods
      .getAmountsOut(`${+ethers.utils.parseUnits(amountIn, token1Decimals)}`, [
        address1,
        address2,
      ])
      .call();

    const amount_out = values_out[1] * 10 ** -token2Decimals;
    return Number(amount_out);
  } catch (error) {
    return false;
  }
}

// This function calls the pair contract to fetch the reserves stored in a the liquidity pool between the token of address1 and the token
// of address2. Some extra logic was needed to make sure that the results were returned in the correct order, as
// `pair.getReserves()` would always return the reserves in the same order regardless of which order the addresses were.
//    `address1` - An Ethereum address of the token to trade from (either a ERC20 token or AUT)
//    `address2` - An Ethereum address of the token to trade to (either a ERC20 token or AUT)
//    `pair` - The pair contract for the two tokens
export async function fetchReserves(address1, address2, pairContract) {
  try {
    // Get decimals for each coin
    const web3 = new Web3(window.ethereum);
    const coin1 = new web3.eth.Contract(ERC20.abi, address1);
    const coin2 = new web3.eth.Contract(ERC20.abi, address2);

    const coin1Decimals = await getDecimals(coin1);
    const coin2Decimals = await getDecimals(coin2);

    // Get reserves
    const reservesRaw = await pairContract.methods.getReserves().call();

    // Put the results in the right order
    const token0 = await pairContract.methods.token0().call();
    const token1 = await pairContract.methods.token1().call();
    const results = [
      token0 === address1 ? reservesRaw[0] : reservesRaw[1],
      token1 === address2 ? reservesRaw[1] : reservesRaw[0],
    ];

    // Scale each to the right decimal place
    return [
      results[0] * 10 ** -coin1Decimals,
      results[1] * 10 ** -coin2Decimals,
    ];
  } catch (err) {
    console.log("error!");
    console.log(err);
    return [0, 0];
  }
}

// This function returns the reserves stored in a the liquidity pool between the token of address1 and the token
// of address2, as well as the liquidity tokens owned by accountAddress for that pair.
//    `address1` - An Ethereum address of the token to trade from (either a token or AUT)
//    `address2` - An Ethereum address of the token to trade to (either a token or AUT)
//    `factory` - The current factory
//    `signer` - The current signer
export async function getReserves(
  address1,
  address2,
  factoryAddress,
  accountAddress
) {
  try {
    const web3 = new Web3(window.ethereum);

    const factoryContract = new web3.eth.Contract(FACTORY.abi, factoryAddress);
    const pairAddress = await factoryContract.methods
      .getPair(address1, address2)
      .call();
    const pairContract = new web3.eth.Contract(PAIR.abi, pairAddress);

    if (pairAddress !== "0x0000000000000000000000000000000000000000") {
      const reservesRaw = await fetchReserves(address1, address2, pairContract);
      const liquidityTokens_BN = await balanceOf(pairContract, accountAddress);
      const liquidityTokens = Number(
        ethers.utils.formatEther(liquidityTokens_BN)
      );

      return [
        reservesRaw[0].toPrecision(6),
        reservesRaw[1].toPrecision(6),
        liquidityTokens,
        pairAddress,
      ];
    } else {
      console.log("no reserves yet");
      return [0, 0, 0, ""];
    }
  } catch (err) {
    console.log("error!");
    console.log(err);
    return [0, 0, 0, ""];
  }
}
